/* eslint-disable react/prop-types, max-len */
import {
  ellipsis, H3, Link, List, nowrap,
  ListItem as DefaultListItem
} from '@codex/basics';
import { replacePlaceholders, resolveByCountry } from '@codex/helpers';
import React from 'react';
import styled, { css } from 'styled-components';
import {
  LAYOUT_1_0,
  LAYOUT_1_2,
  LAYOUT_2_0,
  LAYOUT_2_2
} from '../../constants';
import ProductImage from '../../ProductImage/index';
import StatusLabel from '../../StatusLabel/index';

const MY_ACCOUNT_PATH = resolveByCountry(
  '/gbuk/s/my-account.html',
  '/ieen/s/my-account.html'
);

const ListItem = styled(DefaultListItem)`
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const listRenderer = layout => (item, index) => (
  <ListItem key={index} layout={layout}>
    <Link href={item.url} {...(item.attrs || {})}>{item.label}</Link>
  </ListItem>
);

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  
  > ${H3} {
    font-weight: ${({ theme }) => theme.fontWeights.normal};
    line-height: 1.1;
    
    ${Link} {
      margin-top: ${({ theme }) => theme.spacing[1]};
    }
  }
  
  ${props => props.appearance === 'box' && css`
    border: 1px solid ${props.theme.colors.shades[5]};
    background-color: ${props.theme.colors.shades[7]};
  `};
`;

const Separator = styled.div`
  position: absolute;
  width: 1px;
  height: auto;
  top: 10px;
  bottom: 10px;
  background: #e0e0e0;
  ${({ side }) => `${side}: 25%`};
`;

const Sidebar = styled.div`
  width: 25%;
  padding: ${({ theme }) => `${theme.spacing[2]} 0`};
  ${({ padding }) => `padding-${padding}: 30px`};
  
  > ${H3} {
    font-weight: ${({ theme }) => theme.fontWeights.normal};
  }
`;

const Middle = styled.div`
  display: flex;
  flex-direction: row;
  width: 50%;
  overflow: hidden;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
`;

const MiddleContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 30px;
  overflow: hidden;
  padding: ${({ theme }) => `${theme.spacing[1]} 0`};
`;

const Date = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.shades[1]};
  display: block;
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const ProductLink = styled(Link)`
  display: block;
  line-height: 1.1;
  ${ellipsis()};
  ${nowrap()};
`;

const NoteWrapper = styled.span`
  line-height: 1.1;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.shades[1]};
  font-style: italic;
  margin-top: ${({ theme, layout }) => ([LAYOUT_2_0, LAYOUT_1_0].includes(layout) ? theme.spacing[1] : theme.spacing[2])};
  
  ${({ layout }) => [LAYOUT_2_2].includes(layout) && css`
    margin-right: ${({ theme }) => `-${theme.spacing[2]}`};
    margin-left: ${({ theme }) => `-${theme.spacing[1]}`};
    text-align: center;
    width: 100px;
    
    @media ${({ theme }) => theme.breakpoints.tight} {
      width: 60px;
      margin-right: 0;
      margin-left: 0;
    }
  `};
`;

const Note = props => (
  <NoteWrapper layout={props.layout}>
    {replacePlaceholders(
      props.plusItemsMessage,
      {
        '[NUMBER_OF_ITEMS]': props.numberOfProducts - 1,
        '[S]': props.numberOfProducts > 2 ? 's' : ''
      }
    )}
  </NoteWrapper>
);

const GuestLinkWrapper = styled.span`
  margin-top: ${({ theme, layout }) => (
    [LAYOUT_1_2, LAYOUT_2_0].includes(layout) ? theme.spacing[3] : 0
  )};
    
  strong {
    font-weight: ${({ theme }) => theme.fontWeights.bold};
  }
`;

const Title = props => (props.guest
  ? <span>{props.children}</span>
  : <Link {...props} />
);

const FullWidthItem = props => (
  <Container appearance={props.appearance} className={props.className}>
    <Sidebar padding="right">
      <H3>
        <Title
          guest={props.guest}
          href={`${MY_ACCOUNT_PATH}#order-detail/${props.orderReference}`}
          data-qa="order_number"
          data-interaction="Orders module: order number"
        >
          {replacePlaceholders(
            props.orderTitle,
            {
              '[ORDER_REFERENCE]': props.orderReference
            }
          )}
        </Title>
        <Date data-qa="order_date">{props.orderDate}</Date>
      </H3>
    </Sidebar>
    <Separator side="left" />
    <Middle>
      <Link href={props.mainProduct.productUrl}>
        <ProductImage
          src={props.mainProduct.image}
          alt={props.mainProduct.name}
          data-interaction="Orders module: product image"
          ignoreTight
        />
      </Link>
      <MiddleContent>
        <>
          <StatusLabel dataQa="order_status" status={props.orderStatus} />
          <ProductLink
            data-qa="product_name"
            href={props.mainProduct.productUrl}
          >
            {props.mainProduct.name}
          </ProductLink>
          {props.numberOfProducts > 1 && <Note {...props} />}
        </>
      </MiddleContent>
    </Middle>
    <Separator side="right" />
    <Sidebar padding="left">
      {!props.guest
        ? <List items={props.links}>{listRenderer(props.layout)}</List>
        : (
          <GuestLinkWrapper layout={props.layout}>
            <Link href="#" onClick={props.onRegisterAccountLinkClicked}>{props.registerAccountMessageStart}</Link>{' '}
            <span dangerouslySetInnerHTML={{ __html: props.registerAccountMessageEnd }} />
          </GuestLinkWrapper>
        )}
    </Sidebar>
  </Container>
);

FullWidthItem.displayName = 'RecentOrder-FullWidth';

export default FullWidthItem;
